import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import ContentPage from "@/components/contentPage";
import { calcDateDuration, formatDateRange } from "@/utils";
import {
  fetchMetrics,
} from "@/vmeAiApiClient/adapter";
import SampleDetectionGrid from "@/components/mockROVComponents/sampleDetectionGrid";
import PhylumMetricsDisplay from "@/components/mockROVComponents/phylumMetrics";
import { Explanations } from "@/components/mockROVComponents/explanations";
import { FaInfoCircle } from "react-icons/fa";

export const Route = createFileRoute("/mockRovAssets/$lowering/")({
  component: RovLoweringDetail,
  loader: ({ context: { queryClient }, params: { lowering } }) =>
    queryClient.prefetchQuery({
      queryKey: ["rovMetrics", lowering],
      queryFn: () => fetchMetrics(lowering),
    }),
});

function RovLoweringDetail() {
  const navigate = useNavigate();
  const { lowering } = Route.useParams();

  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery({
    queryKey: ["rovMetrics", lowering],
    queryFn: () => fetchMetrics(lowering),
    enabled: !!lowering,
  });

  if (metrics) {
    metrics.starting_hour = metrics["Start Time"].split("T")[1].split(":")[0];
    metrics.ending_hour = metrics["End Time"].split("T")[1].split(":")[0];
  }

  if (metricsLoading) return <div>Loading...</div>;
  if (metricsError) return <div>Error loading dive data</div>;

  if (!metrics) return <div>Lowering not found</div>;

  return lowering ? (
    <ContentPage title={`AI Report for Lowering: ${lowering}`}>
      <div className="mx-7">
        <div className="mb-6 mt-9 grid grid-cols-3 gap-4 border-b pb-4">
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Expedition
            </h4>
            <p className="text-lg text-blue-400">{metrics.Expedition}</p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Dates
            </h4>
            <p className="text-lg text-blue-400">
              {formatDateRange(metrics["Start Time"], metrics["End Time"])}
            </p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Lowering
            </h4>
            <p className="text-lg text-blue-400">{metrics.Lowering}</p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Camera Analyzed
            </h4>
            <p className="text-lg text-blue-400">
              {metrics["Camera Analysed"]}
            </p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Max Depth
            </h4>
            <p className="text-lg text-blue-400">{metrics["Max Depth"]}m</p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Analyzed Frames Per Second
            </h4>
            <p className="text-lg text-blue-400">
              {metrics["FPS Analysis Rate"]}
            </p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Total Frames Analyzed
            </h4>
            <p className="text-lg text-blue-400">
              {metrics["Total Frames Analysed"]}
            </p>
          </div>
          <div>
            <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">
              Duration
            </h4>
            <p className="text-lg text-blue-400">
              {calcDateDuration(metrics["Start Time"], metrics["End Time"])}
            </p>
          </div>
          <div>
            <div className="flex items-start">
              <h4 className="mb-2 font-bold text-sm uppercase tracking-wide text-blue-100">Transect Length</h4>
              <div>
                <FaInfoCircle
                  className="ml-1 cursor-pointer text-blue-300 hover:text-blue-400 w-3 h-3"
                  onClick={() => {
                    document
                      .querySelector("section")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              </div>
            </div>
            <p className="text-lg text-blue-400">
              {metrics["Total Distance Travelled (m)"]}m
            </p>
          </div>
          <div className="col-span-1 col-start-2 mb-6">
            <div className="rounded">
              <p className="text-center text-lg text-gray-600">
                Overall AI Confidence:{" "}
                <span className="font-bold">
                  {metrics["Overall Confidence"]}
                </span>
              </p>
            </div>
            <div
              className="mt-2 cursor-pointer rounded bg-blue-100 p-2 transition-colors hover:bg-blue-200"
              onClick={() =>
                navigate({ to: `/mockRovAssets/${lowering}/digitalTwin` })
              }
            >
              <p className="text-center font-bold text-white">
                Dive to ROV Digital Twin
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 border-b">
          <PhylumMetricsDisplay phylum="coral" lowering={lowering} />
          <SampleDetectionGrid
            lowering={lowering}
            phylum="coral"
            starting_hour={metrics.starting_hour}
            ending_hour={metrics.ending_hour}
          />
        </div>
        <div className="mt-8 border-b">
          <PhylumMetricsDisplay phylum="sponge" lowering={lowering} />
          <SampleDetectionGrid
            lowering={lowering}
            phylum="sponge"
            starting_hour={metrics.starting_hour}
            ending_hour={metrics.ending_hour}
          />
        </div>
      </div>
      <Explanations />
    </ContentPage>
  ) : (
    <div>Loading...</div>
  );
}
